import * as React from "react"
import { format, setMonth, setYear } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface DatePickerProps {
  // Existing string-based API (backward compatible)
  value?: string
  onChange?: (value: string) => void
  // New Date-based API
  date?: Date
  onDateChange?: (date: Date | undefined) => void
  className?: string
  placeholder?: string
  "data-field"?: string
  disabled?: boolean
}

export function DatePicker({
  value,
  onChange,
  date: dateProp,
  onDateChange,
  className,
  placeholder = "Pick a date",
  "data-field": dataField,
  disabled,
}: DatePickerProps) {
  const today = new Date()
  // Support both APIs: date prop (new) or value string (legacy)
  // Validate legacy string to prevent "Invalid time value" crashes in date-fns.
  let date: Date | undefined = undefined;
  if (dateProp) {
    date = dateProp;
  } else if (value) {
    const parsedDate = new Date(value);
    if (!isNaN(parsedDate.getTime())) {
      date = parsedDate;
    }
  }
  const [month, setMonthState] = React.useState(date?.getMonth() ?? today.getMonth())
  const [year, setYearState] = React.useState(date?.getFullYear() ?? today.getFullYear())

  const displayMonth = setMonth(setYear(today, year), month)

  const handleMonthChange = (newMonth: number) => {
    setMonthState(newMonth)
  }

  const handleYearChange = (newYear: number) => {
    setYearState(newYear)
  }

  // Generate years from 1900 to current year + 10
  const currentYear = today.getFullYear()
  const years = Array.from({ length: currentYear - 1899 + 10 }, (_, i) => 1900 + i)

  return (
    <Popover>
      {/* Avoid `asChild` to prevent Radix composeRefs ref loops */}
      <PopoverTrigger
        data-field={dataField}
        disabled={disabled}
        className={cn(
          buttonVariants({ variant: "ghost" }),
          "w-full justify-end text-right font-normal bg-transparent border-0 p-0 h-auto text-[17px] text-gray-900 dark:text-gray-100 hover:bg-transparent",
          !date && "text-gray-400 dark:text-gray-500",
          className
        )}
      >
        {date ? format(date, "PPP") : <span>{placeholder}</span>}
        <CalendarIcon className="ml-2 h-4 w-4 opacity-50" />
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 rounded-md shadow-apple-md" align="end">
        <div className="p-4 space-y-4">
          {/* Year and Month Dropdowns */}
          <div className="flex gap-3">
            <Select
              value={year.toString()}
              onValueChange={(val) => handleYearChange(Number(val))}
            >
              <SelectTrigger className="w-[140px] bg-gray-50 dark:bg-gray-950 rounded border border-gray-200/40 dark:border-gray-700/40 px-3 py-2.5 text-[15px] font-normal">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent className="rounded-md">
                {years.map((y) => (
                  <SelectItem key={y} value={y.toString()}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={month.toString()}
              onValueChange={(val) => handleMonthChange(Number(val))}
            >
              <SelectTrigger className="w-[140px] bg-gray-50 dark:bg-gray-950 rounded border border-gray-200/40 dark:border-gray-700/40 px-3 py-2.5 text-[15px] font-normal">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent className="rounded-md">
                {Array.from({ length: 12 }, (_, i) => (
                  <SelectItem key={i} value={i.toString()}>
                    {format(new Date(2000, i, 1), "MMMM")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Calendar */}
          <Calendar
            mode="single"
            selected={date}
            onSelect={(selectedDate) => {
              if (selectedDate) {
                // Support both APIs
                if (onDateChange) {
                  onDateChange(selectedDate)
                } else if (onChange) {
                  // Format as YYYY-MM-DD for the string API
                const year = selectedDate.getFullYear()
                const month = String(selectedDate.getMonth() + 1).padStart(2, "0")
                const day = String(selectedDate.getDate()).padStart(2, "0")
                onChange(`${year}-${month}-${day}`)
                }
              } else {
                // Handle deselection
                if (onDateChange) {
                  onDateChange(undefined)
                }
              }
            }}
            month={displayMonth}
            onMonthChange={(date) => {
              setMonthState(date.getMonth())
              setYearState(date.getFullYear())
            }}
            className="rounded-xs border"
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}
